"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.seoUrlAnalysis = exports.seoUrlAnalysisResultSchema = exports.seoUrlAnalysisArgsSchema = exports.urlAnalysisActionMetadata = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// URL Analysis Action Types
var urlAnalysisActions = ['GetPageAuthority'];
// Action metadata for UI guidance with use cases and workflow stages
exports.urlAnalysisActionMetadata = {
    GetPageAuthority: {
        description: 'Get Page Authority and Domain Authority scores for a specific URL',
        useCases: [
            'SEO strength assessment for specific pages and domains',
            'Authority score analysis for link building target evaluation',
            'Page-level SEO performance measurement and tracking',
            'Competitive authority analysis for content and link strategies',
        ],
        workflowStages: ['analysis', 'measurement', 'competitive-research', 'link-building'],
        businessContext: 'Essential for understanding the SEO authority of specific URLs and domains, critical for link building strategies and competitive analysis',
    },
};
exports.seoUrlAnalysisArgsSchema = zod_1.z.object({
    action: zod_1.z
        .enum(urlAnalysisActions)
        .describe('The type of URL analysis to perform:\n' +
        '• GetPageAuthority: Get Page Authority and Domain Authority scores for a specific URL - essential for SEO strength assessment and competitive analysis'),
    url: zod_1.z
        .string()
        .describe('The target URL to analyze. Supports flexible formats - protocol is optional and will be auto-normalized. Examples: "https://example.com", "example.com/page", "www.example.com/article"'),
    emitMode: common_1.emitModeSchema.default('single').describe('The mode to emit the data in.'),
    options: zod_1.z
        .object({
        failIfEmpty: zod_1.z
            .boolean()
            .default(true)
            .describe('Fail if the search returns no results. Default to true.'),
    })
        .describe('Search options'),
});
// Column definitions for each action type
var columnDefinitions = zod_1.z.object({
    GetPageAuthority: zod_1.z
        .object({
        url: zod_1.z.string().describe('The analyzed URL'),
        page_authority_score: zod_1.z.number().describe('Page Authority Score (0-100)'),
        domain: zod_1.z.string().describe('The domain extracted from the URL'),
        domain_authority_score: zod_1.z.number().describe('Domain Authority Score (0-100)'),
    })
        .describe('Columns for GetPageAuthority: page and domain authority scores'),
});
exports.seoUrlAnalysisResultSchema = zod_1.z.object({
    csvData: zod_1.z
        .string()
        .describe('Semicolon-delimited CSV containing URL authority analysis data. GetPageAuthority: URL, page authority score, domain, domain authority score (1 row).'),
    query: zod_1.z.object({
        action: zod_1.z.string().describe('The URL analysis action that was performed'),
        url: zod_1.z.string().describe('The URL that was analyzed'),
    }),
    columnSchema: zod_1.z
        .union([
        columnDefinitions.shape.GetPageAuthority,
        zod_1.z.record(zod_1.z.string()), // Allow simple key-value string pairs for backend compatibility
    ])
        .describe('Schema of the columns returned in csvData based on the action performed. Use this to understand the structure of each row in the CSV.'),
    metadata: zod_1.z.object({
        executedAt: zod_1.z.string().datetime(),
        recordCount: zod_1.z.number().optional().describe('Number of records returned'),
        actionType: zod_1.z
            .enum(['GetPageAuthority'])
            .describe('The specific action type for column schema reference'),
    }),
});
exports.seoUrlAnalysis = (0, common_1.createEmitModeToolMetadata)({
    id: 'seoUrlAnalysis',
    displayName: 'URL Authority Analysis',
    displayDescription: 'URL analysis with authority scoring for comprehensive page and domain authority assessment. Essential for SEO strength evaluation, competitive analysis, and link building strategies.',
    category: 'seo',
    color: '#0066CC',
    backgroundColor: (0, common_1.createBgColor)('#0066CC'),
    icon: 'search',
    argsSchema: exports.seoUrlAnalysisArgsSchema,
}, exports.seoUrlAnalysisResultSchema);
